import './Templates.scss'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { ActivityLog, Collapsible, MultiCheckbox, SearchBox, SupportPaneSize, TemplateSummary } from '@/ux'
import { ErrorBoundary, Select } from '@/ui-legacy'
import clsx from 'clsx'
import { usePlatformOutletContext } from '../Platform/usePlatFormOutletContext'
import { RestSchemas } from '@/contexts/QueryProvider'

type MessagesTemplatesTTemplateActivity = RestSchemas['Messages.Templates.TTemplateActivity']

const TAB_MENU = ['All', 'Accepted', 'Returned', 'Uploaded', 'None']

type TemplatesProps = {
  supportPaneSize: SupportPaneSize | undefined
  setSupportPaneSize: (size: SupportPaneSize | undefined) => void
  appId: number | null
}

const Templates = ({ supportPaneSize, setSupportPaneSize, appId }: TemplatesProps) => {
  const { projectId } = usePlatformOutletContext()
  const { dmpClient } = useDotnetClient()
  const [loading, setLoading] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string>()
  const [selectedTab, setSelectedTab] = useState<string>(TAB_MENU[0])
  const [summary, setSummary] = useState<MessagesTemplatesTTemplateActivity[]>()
  const [entityTypes, setEntityTypes] = useState<Map<string, string[]>>()
  const [entityGroups, setEntityGroups] = useState<string[]>()
  const [selectedEntityType, setSelectedEntityType] = useState<string>()
  const [selectedEntities, setSelectedEntities] = useState<string[]>()
  const [selectedEntityId, setSelectedEntityId] = useState<number>()
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>()

  useEffect(() => {
    const fetchSummary = async () => {
      // TODO - Error check for appId and projectId here
      setLoading(true)
      dmpClient?.templatesApi
        .getTemplatesApiGetTemplateSummary(appId ?? 0, projectId ?? 0)
        .then((result) => {
          setSummary(result)
          deriveEntityTypes(result)
          const entitys = result.map((t) => t.entity.entity_type.name)
          setEntityGroups(entitys.filter((x, i) => entitys?.indexOf(x) === i))
        })
        .catch((err) => {
          console.warn(err)
        })
        .finally(() => setLoading(false))
    }
    fetchSummary()
  }, [appId, dmpClient?.templatesApi, projectId])

  const deriveEntityTypes = (templates: MessagesTemplatesTTemplateActivity[]) => {
    if (!templates || templates.length === 0) {
      return
    }
    const derivedEntityTypes = new Map<string, string[]>()
    templates?.forEach((templateItem) => {
      const entityType = templateItem.entity.entity_type.name
      if (derivedEntityTypes.has(entityType)) {
        const derivedEntities = derivedEntityTypes.get(entityType)
        if (!derivedEntities?.includes(templateItem.entity.name)) {
          derivedEntities?.push(templateItem.entity.name)
        }
        derivedEntityTypes.set(entityType, derivedEntities ?? [])
      } else {
        derivedEntityTypes.set(entityType, [templateItem.entity.name])
      }
    })

    setEntityTypes(derivedEntityTypes)

    const firstEntityType = derivedEntityTypes.entries().next().value
    if (firstEntityType) {
      setSelectedEntityType(firstEntityType[0] as string)
      setSelectedEntities(firstEntityType[1] as string[])
    }
  }

  const toggleEntitySelected = useCallback((option: string) => {
    setSelectedEntities((prev) => {
      const currentSelections = prev ?? []
      const filtered = currentSelections.filter((it) => it !== option)

      return filtered.length < currentSelections.length
        ? // option was filtered out
          filtered
        : // option must be added
          [...filtered, option]
    })
  }, [])

  const filterOnSelectedEntities = (template: MessagesTemplatesTTemplateActivity) =>
    supportPaneSize === SupportPaneSize.Min ||
    (selectedEntities && !!template.entity && selectedEntities?.includes(template.entity.name))

  const filterOnSelectedTab = (template: MessagesTemplatesTTemplateActivity) =>
    selectedTab === 'All' || template.state === selectedTab

  const filterOnSearchTerm = (template: MessagesTemplatesTTemplateActivity) =>
    !searchString || searchString === '' || JSON.stringify(template).toLowerCase().includes(searchString.toLowerCase())

  const onSelectTemplate = (entityId: number, templateId: number, entityType: string) => {
    setSelectedEntityId(entityId)
    setSelectedTemplateId(templateId)

    if (supportPaneSize === SupportPaneSize.Min) {
      setSelectedEntityType(entityType)
      setSelectedEntities(entityTypes?.get(entityType))
    }

    setSupportPaneSize(SupportPaneSize.Max)
  }

  const newLastActivity = (activity: MessagesTemplatesTTemplateActivity) => {
    if (!summary) return
    const oldSummary = summary
    const found = oldSummary?.findIndex(
      (x) => x.file_template.id === selectedTemplateId && x.entity.id === selectedEntityId
    )
    if (found !== -1) {
      oldSummary[found] = activity
      setSummary([...oldSummary])
    }
  }

  const numberOfFiles = () => {
    if (!summary) return ''

    const summaryFiltered = summary?.filter(filterOnSelectedEntities)
    const total = summaryFiltered.length ?? 0
    const string = total === 1 ? 'file' : 'files'

    if (selectedTab === 'All') return `${total} ${string}`
    else {
      const filtered = summaryFiltered.filter(filterOnSelectedTab).length ?? 0
      return `${filtered}/${total} ${string}`
    }
  }

  return (
    <div className={clsx('ux-templates-layout', supportPaneSize)} key="pane1">
      <ErrorBoundary>
        {supportPaneSize === SupportPaneSize.Max && (
          <div className="entity-template-selector">
            <Select
              options={Array.from(entityTypes?.keys() ?? [])}
              selected={selectedEntityType}
              onOptionSelected={(value) => {
                setSelectedEntityType(value)
                setSelectedEntities(entityTypes?.get(value))
              }}
              placeholder={'Entity Type'}
            />
            {selectedEntityType && (
              <MultiCheckbox
                options={entityTypes?.get(selectedEntityType) ?? []}
                selected={selectedEntities ?? []}
                onOptionClicked={toggleEntitySelected}
              />
            )}
          </div>
        )}

        <div className="ux-templates" key="pane2">
          <div className="sticky -top-[0.5rem] z-10 flex flex-col gap-y-[0.5rem] bg-background">
            <div className="tabs">
              {TAB_MENU.map((menuItem) => (
                <Fragment key={menuItem}>
                  <div
                    className={`tab ${selectedTab === menuItem ? 'selected' : ''}`}
                    onClick={() => setSelectedTab(menuItem)}
                    title={menuItem}
                  >
                    {menuItem}
                  </div>
                  <div>|</div>
                </Fragment>
              ))}
            </div>
            <SearchBox searchString={searchString} setSearchString={setSearchString} key="searchTemplates" />
            <div className="file-count" key="file_count">
              {numberOfFiles()}
            </div>
          </div>
          {entityTypes &&
            entityGroups &&
            (supportPaneSize === SupportPaneSize.Min ? (
              entityGroups.map((entityTypeKey) => {
                return (
                  <ErrorBoundary key={entityTypeKey}>
                    <Collapsible title={entityTypeKey} defaultCollapsed={false} key={entityTypeKey}>
                      {summary
                        ?.filter((x) => x.entity.entity_type.name === entityTypeKey)
                        .filter(filterOnSelectedEntities)
                        .filter(filterOnSelectedTab)
                        .filter(filterOnSearchTerm)
                        .map((template) => {
                          return (
                            <TemplateSummary
                              {...template}
                              selected={
                                template.file_template.id === selectedTemplateId &&
                                template.entity.id === selectedEntityId
                              }
                              onClick={onSelectTemplate}
                              appId={appId}
                              key={`${template.file_template.id}-${template.entity.id}`}
                            />
                          )
                        })}
                    </Collapsible>
                  </ErrorBoundary>
                )
              })
            ) : (
              <ErrorBoundary key="eb2">
                {summary
                  ?.filter(filterOnSelectedEntities)
                  .filter(filterOnSelectedTab)
                  .filter(filterOnSearchTerm)
                  .map((template) => {
                    return (
                      <TemplateSummary
                        {...template}
                        selected={
                          template.file_template.id === selectedTemplateId && template.entity.id === selectedEntityId
                        }
                        onClick={onSelectTemplate}
                        appId={appId}
                        key={`${template.file_template.id}-${template.entity.id}`}
                      />
                    )
                  })}
              </ErrorBoundary>
            ))}
          {loading && !summary && <div>Loading...</div>}
          {!loading && !summary && <div>No templates for user</div>}
        </div>
        {supportPaneSize === SupportPaneSize.Max && (
          <ErrorBoundary key="eb3">
            <ActivityLog
              appId={appId}
              entityId={selectedEntityId}
              templateId={selectedTemplateId}
              newLastActivity={newLastActivity}
              key="pane3"
            />
          </ErrorBoundary>
        )}
      </ErrorBoundary>
    </div>
  )
}

export default Templates
